import {
  LEVEL_LABELS,
  MANAGEMENT_RESOURCE,
  MODULES,
  cycleLevel,
  groupsUsingRole,
  isProtectedRole,
  isRoleNameTaken,
  lockedLevel,
  roleAssignmentCounts,
  type Group,
  type Level,
  type Role,
  type TenantResource,
  type TenantUser,
} from '@titan/shared';
import { SHIPPED_MODULES } from '@titan/modules';
import { useState, type CSSProperties } from 'react';
import { useSession } from '../../session/SessionProvider.js';
import { Actions, Button, Card, ChipSelector, ConfirmButton, Field, InlineError, InlineInfo, TextInput } from '../../ui/index.js';
import { useApprovalNotice } from '../access/useNotice.js';
import { errorMessage } from '../useLoad.js';
import styles from './Rbac.module.css';

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(' ');

export interface RbacProps {
  roles: Role[];
  groups: Group[];
  users: TenantUser[];
  /** Staff previewing a tenant pass its id to the org endpoints. */
  staffTenantId?: string;
  readOnly: boolean;
  onChanged: () => Promise<void>;
}

/** Columns: the modules that have shipped, then management. Unshipped modules never appear. */
const RESOURCES: Array<{ key: TenantResource; label: string; note: string; kind: 'module' | 'administration' }> = [
  ...SHIPPED_MODULES.map((k) => ({ key: k, label: MODULES[k].label, note: MODULES[k].description, kind: 'module' as const })),
  { key: 'management', label: MANAGEMENT_RESOURCE.label, note: MANAGEMENT_RESOURCE.description, kind: 'administration' },
];

/**
 * Spec 08 for one tenant, transposed so it scales: roles are rows and grow downward,
 * resources are the few columns. Levels are No access / Read only / Full access. Locked
 * cells cannot be cycled. Assignment counts derive from the user list.
 */
export function RbacMatrix({ roles, groups, users, staffTenantId, readOnly, onChanged }: RbacProps) {
  const { api } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [baseRoleId, setBaseRoleId] = useState<string | null>(null);
  const q = staffTenantId ? `?tenantId=${encodeURIComponent(staffTenantId)}` : '';
  const counts = roleAssignmentCounts(users, groups, roles);
  const { notice, check, clear } = useApprovalNotice();

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    clear();
    try {
      check(await work());
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const cycle = (role: Role, resource: TenantResource) => {
    if (readOnly || lockedLevel(role, resource) !== null) return;
    const next: Level = cycleLevel(role.levels[resource]);
    void run(() => api(`/org/roles/${role.id}${q}`, { method: 'PATCH', body: JSON.stringify({ levels: { [resource]: next } }) }));
  };

  const createRole = () =>
    run(async () => {
      await api(`/org/roles${q}`, { method: 'POST', body: JSON.stringify({ name: roleName.trim(), ...(baseRoleId ? { baseRoleId } : {}) }) });
      setRoleName('');
      setBaseRoleId(null);
    });

  const roleTaken = isRoleNameTaken(roleName, roles);
  const vars = { '--cols': RESOURCES.length } as CSSProperties;

  return (
    <>
      {error && <InlineError>{error}</InlineError>}
      {notice && <InlineInfo>{notice}</InlineInfo>}

      <Card title="Levels" description="A role is a set of levels, one per resource. There is no permission that lives outside this matrix.">
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span className={styles.cell}>{LEVEL_LABELS.none}</span>
            <span>The module does not appear.</span>
          </div>
          <div className={styles.legendItem}>
            <span className={cx(styles.cell, styles.cellRead)}>{LEVEL_LABELS.read}</span>
            <span>Reads everything, changes nothing.</span>
          </div>
          <div className={styles.legendItem}>
            <span className={cx(styles.cell, styles.cellFull)}>{LEVEL_LABELS.full}</span>
            <span>Acts inside the module. On management, invites people and assigns groups and roles.</span>
          </div>
        </div>
      </Card>

      <Card
        variant="wide"
        title="Permission matrix"
        description={
          readOnly
            ? 'Levels are set by your administrators and by DinoCloud.'
            : 'One row per role. Click a cell to cycle no access, read only, full access. Locked cells protect the role model. A change that gives people management access waits for a second person. Superadmins always hold full access and are not a row.'
        }
      >
        {SHIPPED_MODULES.length === 0 && <span className={styles.note}>Product modules appear as columns here as each one ships. Today the only resource is management.</span>}
        <div className={styles.scroll}>
          <div className={styles.matrix} style={vars} role="table" aria-label="Permission matrix">
            <div className={styles.colHead} role="columnheader" style={{ alignItems: 'flex-start' }}>
              <span className={styles.kind}>role</span>
            </div>
            {RESOURCES.map((res) => (
              <div key={res.key} className={styles.colHead} role="columnheader" title={res.note}>
                <span className={cx(styles.kind, res.kind === 'administration' && styles.kindAdmin)}>{res.kind}</span>
                <span className={styles.colName}>{res.label}</span>
              </div>
            ))}
            <div />
            {roles.map((r) => {
              const used = groupsUsingRole(r.id, groups);
              return (
                <RoleRow key={r.id} role={r} used={used} assigned={counts[r.id] ?? 0} readOnly={readOnly || busy} onCycle={cycle}>
                  {!readOnly && !isProtectedRole(r) && (
                    <ConfirmButton
                      label="Delete"
                      variant="link"
                      tone="critical"
                      disabled={busy}
                      message={
                        used.length > 0
                          ? `${used.length} ${used.length === 1 ? 'group uses' : 'groups use'} this role: ${used.map((g) => g.name).join(', ')}. ${counts[r.id] ?? 0} ${counts[r.id] === 1 ? 'person loses' : 'people lose'} its levels.`
                          : 'No group uses this role. Deleting it changes nobody’s access.'
                      }
                      confirmLabel="Delete role"
                      onConfirm={() => run(() => api(`/org/roles/${r.id}${q}`, { method: 'DELETE' }))}
                    />
                  )}
                </RoleRow>
              );
            })}
          </div>
        </div>
      </Card>

      {!readOnly && (
        <Card variant="form" title="Create a role" description="Start from an existing role, then adjust its row in the matrix. Roles belong to groups; people belong to groups.">
          <Field label="Role name" htmlFor="role-name">
            <TextInput id="role-name" value={roleName} onChange={(e) => setRoleName(e.target.value)} placeholder="Auditor" invalid={roleTaken} />
          </Field>
          <Field label="Start from" chips hint="Leave it unselected to start with no access anywhere.">
            <ChipSelector options={roles.map((r) => ({ value: r.id, label: r.name }))} value={baseRoleId} onChange={setBaseRoleId} />
          </Field>
          {roleTaken && <InlineError>A role with that name already exists.</InlineError>}
          <Actions>
            <Button onClick={createRole} disabled={busy || !roleName.trim() || roleTaken}>
              Create role
            </Button>
          </Actions>
        </Card>
      )}
    </>
  );
}

function RoleRow({
  role,
  used,
  assigned,
  readOnly,
  onCycle,
  children,
}: {
  role: Role;
  used: Group[];
  assigned: number;
  readOnly: boolean;
  onCycle: (role: Role, resource: TenantResource) => void;
  children?: React.ReactNode;
}) {
  return (
    <>
      <div className={styles.rowLine} />
      <div className={styles.rowHead} role="rowheader">
        <span className={styles.roleName}>
          {role.name}
          {isProtectedRole(role) ? ' · built in' : ''}
        </span>
        <span className={styles.roleMeta}>
          {assigned} assigned · {used.length === 0 ? 'not used by any group' : `used by ${used.map((g) => g.name).join(', ')}`}
        </span>
        {role.description && <span className={styles.roleMeta}>{role.description}</span>}
      </div>
      {RESOURCES.map((res) => {
        const level = role.levels[res.key];
        const locked = lockedLevel(role, res.key) !== null;
        return (
          <button
            key={res.key}
            type="button"
            role="cell"
            className={cx(styles.cell, level === 'read' && styles.cellRead, level === 'full' && styles.cellFull, (readOnly || locked) && styles.cellLocked)}
            disabled={readOnly || locked}
            aria-label={`${role.name} on ${res.label}: ${LEVEL_LABELS[level]}${locked ? ', locked' : ''}`}
            title={locked ? 'Locked cells protect the role model.' : undefined}
            onClick={() => onCycle(role, res.key)}
          >
            {LEVEL_LABELS[level]}
            {locked ? ' · locked' : ''}
          </button>
        );
      })}
      <div className={styles.rowActions}>{children}</div>
    </>
  );
}
